import type { ColumnDef } from "@tanstack/react-table";
import { formatIntToCurrency } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import type { ClosingReport } from "~/managers/use-register-manager";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { Button } from "~/components/ui/button";
import { api } from "~/lib/api";
import { useRegisterManager } from "~/managers/use-register-manager";
import { useReportStore } from "~/stores/use-report-store";

export const Route = createFileRoute("/_main/reports/_layout/closing")({
  component: ClosingReports,
});

function ClosingReports() {
  const { range } = useReportStore();
  const { printClosingReport, downloadClosingReport } = useRegisterManager();

  const { t } = useTranslation("common");

  const { data } = api.report.getClosingReports.useQuery(
    {
      startDate: range.from,
      endDate: range.to,
    },
    {
      initialData: [],
      staleTime: 0,
    },
  );

  const columns: ColumnDef<ClosingReport>[] = [
    {
      id: t("Report.number"),
      accessorKey: "number",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.number")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.number}</p>;
      },
    },
    {
      id: t("Report.period_start"),
      accessorKey: "periodStart",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.period_start")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{format(report.periodStart, "Pp")}</p>;
      },
    },
    {
      id: t("Report.period_end"),
      accessorKey: "periodEnd",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.period_end")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{format(report.periodEnd, "Pp")}</p>;
      },
    },
    {
      id: t("Report.revenue"),
      accessorKey: "grossTotal",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.revenue")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{formatIntToCurrency(report.grossTotal)}</p>;
      },
    },
    {
      id: t("Report.receipts"),
      accessorKey: "receipts",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.receipts")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.receipts}</p>;
      },
    },
    {
      id: "Actions",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Actions" />
      ),
      cell: ({ row: { original: report } }) => {
        return (
          <div className="flex flex-row gap-2 items-center">
            <button onClick={() => printClosingReport(report)}>Print</button>
            <button onClick={() => downloadClosingReport(report)}>
              Download
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        buttons={[
          <Button variant="outline" onClick={() => printClosingReport(data)}>
            Print
          </Button>,
          <Button variant="outline" onClick={() => downloadClosingReport(data)}>
            Download
          </Button>,
        ]}
        columns={columns}
        data={data}
      />
    </>
  );
}
